import { DownloadLinkDto } from "../../dtos/DownloadLinkDto";
import { FileDto } from "../../dtos/FileDto";
import { UploadOutcomeDto } from "../../dtos/UploadOutcomeDto";
import { Modules } from "../../dtos/Modules";
import { ApiResult, FileBlob, apiWrapperWithError, generateUrl, getAccessToken } from "./ApiUtils";


export const getFileInfo = async (fileId: string): Promise<ApiResult<FileDto> | null> => {
    return apiWrapperWithError<FileDto>(`api/files/${fileId}`, 'GET', `fetching file info with ID ${fileId}`);
}

export const downloadFile = async (fileId: string): Promise<ApiResult<Blob> | null> => {
    return apiWrapperWithError<Blob>(`api/files/${fileId}/download`, 'GET', `fetching file blob with ID ${fileId}`);
}

// Download a file along with the server's presentation name (Content-Disposition). The server
// applies the FY/step prefix for Submission files and returns the plain stored name otherwise,
// so this is the source of truth for the download file name. fileName may be "" if the header
// is missing; callers should fall back to the stored name.
export const downloadFileWithName = async (fileId: string): Promise<ApiResult<FileBlob> | null> => {
    try {
        const response = await fetch(generateUrl(`api/files/${fileId}/download`), {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${getAccessToken()}` },
        });
        if (!response.ok) {
            return { data: null, error: new Error(`HTTP error! Status: ${response.status} ${response.statusText}`) };
        }
        const blob = await response.blob();
        const fileName = parseContentDispositionFilename(response.headers.get('Content-Disposition') || '');
        return { data: { blob, fileName }, error: null };
    } catch (error) {
        return { data: null, error: error as Error };
    }
}

// A short-lived link the browser opens directly, so it streams the file itself with its own
// save dialog and progress. 404 while streaming file transfer is off.
export const createDownloadLink = async (fileId: string): Promise<ApiResult<DownloadLinkDto> | null> => {
    return apiWrapperWithError<DownloadLinkDto>(`api/files/${fileId}/download-link`, 'POST', `creating download link for file ${fileId}`, {});
}

// Pulls the download file name from a Content-Disposition header, preferring the RFC 5987
// filename*=UTF-8'' form (percent-decoded), falling back to the plain filename= value.
const parseContentDispositionFilename = (disposition: string): string => {
    const star = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
    if (star?.[1]) {
        try { return decodeURIComponent(star[1].trim().replace(/^["']|["']$/g, '')); } catch { /* fall through */ }
    }
    const plain = disposition.match(/filename="?([^";]+)"?/i);
    return plain?.[1]?.trim() ?? '';
}

// The upload was cancelled in the browser; the server drops whatever it had in flight for it.
export const abandonUpload = async (uploadId: string): Promise<ApiResult<boolean> | null> => {
    return apiWrapperWithError<boolean>(`api/files/upload/${uploadId}`, 'DELETE', `abandoning upload ${uploadId}`, {});
}

// How an upload ended once the browser has stopped waiting on its last chunk.
export const getUploadOutcome = async (uploadId: string): Promise<ApiResult<UploadOutcomeDto> | null> => {
    return apiWrapperWithError<UploadOutcomeDto>(`api/files/upload/${uploadId}`, 'GET', `checking upload ${uploadId}`);
}

export const downloadDefaultFile = async (stepId: number, fileId: string): Promise<ApiResult<Blob> | null> => {
    return apiWrapperWithError<Blob>(`api/files/step/${stepId}/defaults/download?fileName=${fileId}`, 'GET', `fetching file blob with ID ${fileId}`);
}

export const deleteFile = async (fileId: string): Promise<ApiResult<boolean> | null> => {
    return apiWrapperWithError<boolean>(`api/files/${fileId}`, 'DELETE', `deleting file with ID ${fileId}`, {});
}

export const deleteDefaultFile = async (stepId: number, fileId: string): Promise<ApiResult<boolean> | null> => {
    return apiWrapperWithError<boolean>(`api/files/step/${stepId}/defaults?fileName=${fileId}`, 'DELETE', `deleting file with ID ${fileId}`, {});
}

export const getFiles = async (stepId: string): Promise<ApiResult<FileDto[]> | null> => {
    return apiWrapperWithError<FileDto[]>(`api/files/step/${stepId}`, 'GET', `fetching files for step with ID ${stepId}`);
}

export const getRequestFiles = async (stepId: string): Promise<ApiResult<FileDto[]> | null> => {
    return apiWrapperWithError<FileDto[]>(`api/files/step/${stepId}/request`, 'GET', `fetching request files for step with ID ${stepId}`);
}

export const getResponseFiles = async (stepId: string): Promise<ApiResult<FileDto[]> | null> => {
    return apiWrapperWithError<FileDto[]>(`api/files/step/${stepId}/response`, 'GET', `fetching response files for step with ID ${stepId}`);
}

export const getPhaseFiles = async (phaseId: string, module?: Modules): Promise<ApiResult<FileBlob> | null> => {
    return apiWrapperWithError<FileBlob>(`api/files/phase/${phaseId}/files/${module}`, 'GET', `downloading phase files with ID ${phaseId}`);
}

export const downloadPhaseRequestResponseReport = async (phaseId: string): Promise<ApiResult<Blob> | null> => {
    return apiWrapperWithError<Blob>(`api/files/phase/${phaseId}/request-response-report`, 'GET', `downloading phase request response report with ID ${phaseId}`);
}

export const getStepFiles = async (stepId: string, module?: Modules): Promise<ApiResult<FileBlob> | null> => {
    return apiWrapperWithError<FileBlob>(`api/files/step/${stepId}/files/${module}`, 'GET', `downloading step files with ID ${stepId}`);
}

export const getDefaultStepFiles = async (stepId: string, module?: Modules): Promise<ApiResult<FileBlob> | null> => {
    return apiWrapperWithError<FileBlob>(`api/files/step/${stepId}/files-defaults/${module}`, 'GET', `downloading step files with ID ${stepId}`);
}